import { createApiCall } from './index'
import type { CertificateHideableField, ProductCustomFieldType } from '@repo/types'
import type { Product } from 'types'

export type SyncProductFromShopifyResponse = Product

export type ProductSyncMode = 'inherit' | 'always_sync' | 'locked'

export interface ProductCustomField {
  key: string
  label: string
  value: string | null
  order: number
  visible: boolean
  /**
   * VRG-104 — what KIND of field this is, which decides WHERE it renders on the
   * certificate. Optional because `custom_fields` is a jsonb column whose pre-VRG-104
   * rows have no `type`; the API always writes a concrete one, so an absent value only
   * ever means "legacy row", read as `text`. See `ProductCustomFieldType`.
   */
  type?: ProductCustomFieldType
}

export const productApi = {
  async syncFromShopify(productId: string): Promise<SyncProductFromShopifyResponse> {
    const response = await createApiCall({
      method: 'PUT',
      url: `/products/admin/${productId}/sync-from-shopify`,
    })

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to sync product from Shopify')
    }

    return response.data
  },

  // VRG-102 — set the per-product provider→Vera re-sync override.
  async setSyncMode(productId: string, syncMode: ProductSyncMode): Promise<Product> {
    const response = await createApiCall({
      method: 'PUT',
      url: `/products/admin/${productId}/sync-mode`,
      data: { sync_mode: syncMode },
    })

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to update sync mode')
    }

    return response.data
  },

  // VRG-101 — replace a product's curated custom-fields list.
  async setCustomFields(productId: string, customFields: ProductCustomField[]): Promise<Product> {
    const response = await createApiCall({
      method: 'PUT',
      url: `/products/admin/${productId}/custom-fields`,
      data: { custom_fields: customFields },
    })

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to update custom fields')
    }

    return response.data
  },

  // VRG-788 — replace the set of STANDARD certificate fields this SKU hides.
  async setCertificateFields(
    productId: string,
    hiddenFields: CertificateHideableField[],
  ): Promise<Product> {
    const response = await createApiCall({
      method: 'PUT',
      url: `/products/admin/${productId}/certificate-fields`,
      data: { hidden_certificate_fields: hiddenFields },
    })

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to update certificate fields')
    }

    return response.data
  },
}
